import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { getProductType } from "../validation/productTypeValidator";
import { ProductTypeEnum } from "../validation/productTypeEnum";
import { Project, ProjectType, Resource, ResourceUploadState, Track, TrackGroup, TrackResource } from "../api";
import { ITrack } from "../Projects/MapData";
import { trackMapConfig } from "../Projects/Views/Drafts/UPC_Tracks/trackMapConfig";
import { GetProjectsApi } from "../api/apiConfig";
import { useStore } from "../State/zustandStore";
import { CommandAction, CprsDestination, ExecutionState } from "../asset-service-api";
import { ITrackView, transformTrackGroups, transformTracks } from "./trackView";
import { getValidationCommand, getIngestCommand, useMutateJobs, useIngestionJobs } from "./ingest.api";
import { isAudioMode } from "./projectTools";

interface FilesAddedEvent {
    resourcesAdded: number,
    resourcesReplaced: number,
    resourcesRemoved: number,
    tooManyResources: number,
}

function formatLogError(error: AxiosError, projectId: string, fnName: string) {
    const messages = [] as string[];

    //@ts-ignore
    if (error.response?.data?.errors) {
    //@ts-ignore
        const keys = Object.getOwnPropertyNames(error.response.data.errors);

        keys.forEach(element => {
        //@ts-ignore
            messages.push(`   ${element}: ${error.response.data.errors[element][0]}`);
        });
        return `${fnName} error:\n${messages.join("\n")}`;
    } else {
        return `${fnName} error: ${error.response?.data ?? error.message}`;
    }

}

// Execute mutation, passed project ids will be deleted and cache updated to returned value
const useMutateBatchDelete = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ projectIds } : { projectIds: string[] }) => {
            const api = await GetProjectsApi();
            return await api.deleteBatch(projectIds);
        },
        onSuccess: (response) => { // Set the projects cache to returned value
            queryClient.setQueryData(["projects"], (old: Project[]) => {
                return response.data;
            });
        },
    });
};

// Execute mutation, add or update result to projects cache, and set in project/id cache
const useMutateProject = () => {
    const queryClient = useQueryClient();
    const {logProjectEvent, setProjectBusy, busy} = useStore().projects;
    return useMutation({
        mutationFn: ({ fn, projectId, ignoreErrors, ignoreLock } : { fn: (...args : any[]) => Promise<AxiosResponse<Project, any>>, projectId: string | undefined, ignoreErrors?: number[] | undefined, ignoreLock?: boolean}) => {
            if (projectId) {
                if (busy.has(projectId)) { // Don't call the update if project api is busy
                    if (ignoreLock) {
                        logProjectEvent(projectId, "Updating project with lock");
                    } else {
                        logProjectEvent(projectId, "Project not updated due to lock");
                        return Promise.resolve({ status: 204 } as AxiosResponse<Project>);
                    }
                }

                setProjectBusy(projectId, true);
            }
            return fn();
        },
        onSuccess: (response, variables) => { // populate the cache with the new project
            queryClient.setQueryData(["projects"], (old: Project[]) => {
                if (response.status !== 204) {
                    if (old.find(o => o.projectId === response.data.projectId)) {
                        return old.map(o => o.projectId === response.data.projectId ? response.data : o);
                    }
                    return old.concat([response.data]);
                }
                return old;
            });

            queryClient.invalidateQueries({ queryKey: ["project", response.data?.projectId ?? variables.projectId]});
        },
        onError: (error, variables) => {
            if (variables.projectId) {
                logProjectEvent(variables.projectId, formatLogError(error as AxiosError, variables.projectId!, variables.fn.name));
            }

            // @ts-expect-error
            if (error.response?.status !== 409 && !variables.ignoreErrors?.includes(error.response?.status)) {
                // @ts-expect-error
                toast.error(`An error occurred updating the project. ${error.response?.data ?? error.message ?? error.response?.status}`, { autoClose: 10000 });
            }
        },
        onSettled: (project, error, variables) => {
            const projectId = project?.data?.projectId ?? variables.projectId!;

            setProjectBusy(projectId, false);
        },
    });
};

// Execute mutation, remove from projects cache.  stale project/id will be removed automatically
const useMutateProjectDelete = () => {
    const queryClient = useQueryClient();
    const {logProjectEvent, setProjectBusy, busy} = useStore().projects;
    return useMutation({
        mutationFn: async ({ projectId } : { projectId: string }) => {
            if (busy.has(projectId)) { // Don't call the delete if project api is busy
                logProjectEvent(projectId, "Project not deleted due to lock");
                return Promise.resolve();
            }

            setProjectBusy(projectId, true);

            const api = await GetProjectsApi();
            return await api._delete(projectId);
        },
        onSuccess: (response, variables) => { // remove the project from the cache
            queryClient.setQueryData(["projects"], (old: Project[]) => {
                return old.filter(o => o.projectId !== variables.projectId);
            });
        },
        onError: (error, variables) => {
            if (variables.projectId) {
                logProjectEvent(variables.projectId, formatLogError(error as AxiosError, variables.projectId!, "_delete"));
            }
        },
        onSettled: (project, error, variables) => {
            setProjectBusy(variables.projectId, false);
        },
    });
};

const useProject = (projectId: string | undefined | null) => {
    const {logProjectEvent} = useStore().projects;
    const projectQuery = useQuery({
        queryKey: ["project", projectId ?? undefined],
        queryFn: async () => {
            const api = await GetProjectsApi();

            try {
                return (await api.getProject(projectId!)).data;
            } catch (error) {
                logProjectEvent(projectId!, formatLogError(error as AxiosError, projectId!, "getProject"));

                // @ts-expect-error
                const errMsg = error.response?.data?.errors?.projectId ?? error.response?.data?.message ?? error.response?.data;

                // @ts-expect-error
                toast(`Error: ${error.response?.status} ${errMsg}`, { type: "error" });
                return {};
            }
        },
        enabled: projectId !== null && projectId !== undefined,
    });
    return projectQuery.data;
};

export const useCreateProject = () => {
    const mutateProject = useMutateProject();
    const store = useStore().projects;

    async function createProject() {
        const api = await GetProjectsApi();
        const createProject = () => api.createProject();
        const response = await mutateProject.mutateAsync({ fn: createProject, projectId: "" });
        const project = response.data;

        store.logProjectEvent(project.projectId!, "Created project");
        store.setSelectedProjectId(project.projectId!);
    }
    return {
        createProject,
    };
};

export const hasMultipleTrackGroups = (project: Project) => {
    const productType = getProductType(project!);
    const multipleTrackGroups = productType === ProductTypeEnum.Vinyl || productType === ProductTypeEnum.Cassette ||
        productType === ProductTypeEnum.DVD || productType === ProductTypeEnum.BluRay;
    return multipleTrackGroups;
};

export const allowMappingFiles = (project: Project) => {
    const productType = getProductType(project!);
    return !(productType === ProductTypeEnum.DVD || productType === ProductTypeEnum.BluRay || productType === ProductTypeEnum.CDAudio);
};

export const hideAutoMap = (project: Project) => {
    const productType = getProductType(project!);
    return productType === ProductTypeEnum.Vinyl || productType === ProductTypeEnum.Cassette ||
            productType === ProductTypeEnum.DVD || productType === ProductTypeEnum.BluRay || productType === ProductTypeEnum.CDAudio ||
            project.projectType === ProjectType.Resource;
};

export const hideTotalDuration = (project: Project) => {
    const productType = getProductType(project!);
    return productType === ProductTypeEnum.DVD || productType === ProductTypeEnum.BluRay;
};

export const getTracks = (project: Project) => {
    if (project?.projectType === ProjectType.Resource) {
        const resourceInfo = project?.resourceInfo;
        const track = {
            groupNumber: 0,
            number: 0,
            title: resourceInfo?.title,
            versionTitle: resourceInfo?.version,
            isrc: resourceInfo?.isrc,
            duration: resourceInfo?.duration,
        } as Track;
        return [track];
    }

    const releaseInfo = project.releaseInfo;
    const tracks = releaseInfo?.trackGroups.reduce((a, c) => c.tracks, [] as Track[]) ?? new Array<Track>();
    return tracks;
};

export const getTrackGroups = (project: Project) => {
    const releaseInfo = project.releaseInfo;
    return releaseInfo?.trackGroups ?? new Array<TrackGroup>();
};

export const getMappedTrack = (project: Project, resource: Resource): ITrackView | undefined => {
    const mappedTrackId = project?.trackResourceMap?.find(x => x.resourceId === resource.id)?.trackId;

    if (mappedTrackId !== undefined && project) {
        const hideDuration = hideTotalDuration(project);
        const projectTracks = hasMultipleTrackGroups(project)
            ? transformTrackGroups(getTrackGroups(project), hideDuration)
            : transformTracks(getTracks(project));
        return projectTracks?.find(x => x.number === mappedTrackId);
    }
};

export const useProjects = (selectedProjectId: string | null | undefined) => {
    const projectStore = useStore((state) => state.projects);
    const mutateProject = useMutateProject();
    const mutateProjectDelete = useMutateProjectDelete();
    const mutateJobs = useMutateJobs();
    const project = useProject(selectedProjectId);
    const queryClient = useQueryClient();
    const jobs = useIngestionJobs();
    const mutateBatchDelete = useMutateBatchDelete();

    async function validate(validateProject: Project, source: string, forceValidation: boolean, sentToDraft: boolean = false) {
        if (validateProject.projectType === ProjectType.Unknown) {
            return;
        }

        if ((!forceValidation || sentToDraft) && validateProject.jobId) {
            const job = jobs.getJob(validateProject.jobId!);

            if (job.command?.action === CommandAction.Ingest) {
                projectStore.logProjectEvent(validateProject.projectId!, `Prevented validate due to previous ingestion ${job.id}`);

                // AT-4364 : We do not need to validate just after SendToDraft is clicked.
                //           But, we need to set the existing Job Action to "Validate"
                //           so that project appear into Draft folder.
                if (sentToDraft) {
                    job.command.action = CommandAction.Validate;
                    return job.id;
                }
                return;
            }
        }

        const command = await getValidationCommand(validateProject);

        const response = await mutateJobs.mutateAsync({ command, projectId: validateProject.projectId!, source });
        return response.data.id;
    }

    async function ingest(ingestProject: Project, source: string, selectedCprsShipments?: string[]) {
        const command = await getIngestCommand(ingestProject);

        if (selectedCprsShipments && selectedCprsShipments.length > 0) {
            const shipments = [] as CprsDestination[];

            for (const selection of selectedCprsShipments!) {
                const upcManufactureId = selection.split("|");
                let cprs = shipments.find(s => s.upc === upcManufactureId[0]);

                if (!cprs) {
                    cprs = {upc: upcManufactureId[0], ids: []} as CprsDestination;
                    shipments.push(cprs);
                }

                cprs.ids!.push(Number(upcManufactureId[1]));
            }

            command.cprsDestinations = shipments;
        }

        const response = await mutateJobs.mutateAsync({ command, projectId: ingestProject.projectId!, source });
        return response.data.id;
    }

    async function recentJobFinished(jobId: string): Promise<boolean> {
        const job = await jobs.getJob(jobId!);

        switch (job.state) {
        case ExecutionState.Completed:
        case ExecutionState.Validated:
        case ExecutionState.ValidationFailed:
        case ExecutionState.Exception:
        case ExecutionState.Suspended:
        case ExecutionState.Unknown:
            return true;
        }
        return false;
    }

    async function setJobId(projectId: string, jobId: string, isIngest: boolean): Promise<Project> {
        const api = await GetProjectsApi();
        const setJobId = () => api.setJobId(projectId, jobId, isIngest, new Date().toISOString());
        return await mutateProject.mutateAsync({ fn: setJobId, projectId: projectId }) as Project;
    }

    async function executeMutation(apiFunction: () => Promise<AxiosResponse<Project, any>>, revalidate: boolean, projectId: string, ignoreErrors?: number[] | undefined, ignoreLock?: boolean) {
        const response = await mutateProject.mutateAsync({ fn: apiFunction, projectId: projectId, ignoreErrors, ignoreLock });
        let updatedProject = response.data;

        if (revalidate && updatedProject && updatedProject.projectType && updatedProject.projectType !== ProjectType.Unknown) {
            const validateResponse = await validate(updatedProject, "project update", false);

            if (validateResponse) {
                updatedProject = await setJobId(updatedProject.projectId!, validateResponse, false);
            }
        }
        return updatedProject;
    }

    async function setIsIngestRequestedAfterUpload(state: boolean, passedProject?: Project, sentToDraft: boolean = false) {
        const targetProject = passedProject ?? project;

        if (targetProject) {
            const api = await GetProjectsApi();
            let validateResponse = undefined as string | undefined;

            if (!state) {
                validateResponse = await validate(targetProject, "setIsIngestRequestedAfterUpload", true, sentToDraft);
            }

            const setIsIngestRequestedAfterUpload = () => api.setIsIngestRequestedAfterUpload(targetProject.projectId!, state, validateResponse, validateResponse ? new Date().toISOString() : undefined);
            const updatedProject = await executeMutation(setIsIngestRequestedAfterUpload, false, targetProject.projectId!);
            return updatedProject;
        }
    }

    async function revalidateProject(source: string, passedProject?: Project) {
        const targetProject = passedProject ?? project;

        if (targetProject && targetProject.projectType !== ProjectType.Unknown) {
            const validateResponse = await validate(targetProject, source, false);

            if (validateResponse) {
                await setJobId(targetProject.projectId!, validateResponse, false);
            }
        }
    }

    async function deleteResources(resources: Resource[]) {
        if (project && project.resources) {
            const removedResources = resources.map(r => r.id!);

            const api = await GetProjectsApi();
            const deleteResources = () => api.deleteResources(project.projectId!, removedResources);
            const updatedProject = await executeMutation(deleteResources, true, project.projectId!);
            return updatedProject;
        }
    }

    async function setProjectNotes(notes: string) {
        if (project) {
            const api = await GetProjectsApi();
            const setNotes = () => api.setProjectNotes(project.projectId!, notes);
            const updatedProject = await executeMutation(setNotes, false, project.projectId!); // project notes do not get revalidated automatically
            return updatedProject;
        }
    }

    async function setProjectIdentifier(identifier: string) {
        if (project) {
            projectStore.logProjectEvent(project.projectId!, `Set project identifier ${identifier}`);

            const api = await GetProjectsApi();
            const setIdentifier = () => api.setProjectIdentifier(project.projectId!, identifier);
            const newProject = await executeMutation(setIdentifier, true, project.projectId!, [404]);
            return newProject;
        }
    }

    async function setOverrideRestrictions(overrideRestrictions: boolean) {
        if (project) {
            const api = await GetProjectsApi();
            const setOverrideRestrictions = () => api.setProjectOverrideRestrictions(project?.projectId!, overrideRestrictions);
            return await executeMutation(setOverrideRestrictions, true, project.projectId!);
        }
    }

    async function duplicateProject(projectId: string) {
        const api = await GetProjectsApi();
        const duplicateProject = () => api.duplicateProject(projectId);

        const newProject = (await mutateProject.mutateAsync({ fn: duplicateProject, projectId })).data;

        projectStore.logProjectEvent(newProject.projectId!, `Created duplicate project from ${projectId}`);
        projectStore.setSelectedProjectId(newProject.projectId!);
    }

    async function deleteProject(project: Project) {
        if (projectStore.selectedProjectId === project.projectId) {
            projectStore.clearSelectedProjectId();
        }

        if (project.projectId) {
            await mutateProjectDelete
                .mutateAsync({ projectId: project.projectId! });
            projectStore.clearProjectLog(project.projectId!);
        }
    }

    async function batchDelete(projectIds: string[]) {
        if (projectIds.includes(selectedProjectId ?? "")) {
            projectStore.clearSelectedProjectId();
        }
        return await mutateBatchDelete.mutateAsync({ projectIds });
    }

    // This is called from the DnD callback, which has a stale closure so
    // it uses the project id as a param instead of the selected project id
    async function addResources(projectId: string, resources: Resource[]) {
        const api = await GetProjectsApi();
        const addResources = () => api.addResources(projectId, resources);
        const updatedProject = await executeMutation(addResources, false, projectId!); // Do not revalidate when adding resources
        return updatedProject;
    }

    async function unmapResource(resourceId: string) {
        if (project) {
            const api = await GetProjectsApi();
            const unmapResource = () => api.unmapResource(project.projectId!, resourceId);
            return await mutateProject.mutateAsync({ fn: unmapResource, projectId: project.projectId! });
        }
    }

    async function mapResourceToTrack(resourceId: string, trackNo: number) {
        if (project) {
            const api = await GetProjectsApi();
            const mapTrack = () => api.mapResource(project?.projectId!, trackNo, resourceId);
            return await executeMutation(mapTrack, true, project.projectId!);
        }
    }

    async function mapResourcesToTrack(resourceTracks : TrackResource[]) {
        if (project) {
            const api = await GetProjectsApi();
            let updatedProject = project;

            const mapTrack = () => api.mapTrackResources(project.projectId!, resourceTracks);

            updatedProject = await executeMutation(mapTrack, true, project.projectId!);
            return updatedProject;
        }
    }

    // Called from aspera handler, not necessarily tied to the selected project
    async function updateUploadStates(projectId: string, uploadStates: ResourceUploadState[]) {
        const api = await GetProjectsApi();
        const updateResource = () => api.updateUploadStates(projectId, uploadStates);
        const updatedProject = await executeMutation(updateResource, false, projectId!, [404], true);
        return updatedProject;
    }

    const resetMap = async () => {
        try {
            if (project) {
                const api = await GetProjectsApi();
                const clearMappedResources = () => api.clearMappedResources(project.projectId!);
                return await mutateProject.mutateAsync({ fn: clearMappedResources, projectId: project.projectId! });
            }
        } catch (error) {
            projectStore.logProjectEvent(project?.projectId!, `Reset map error ${error}`);
        }
    };

    function isResourceMapped(resource: Resource) {
        const resourceMap = project?.trackResourceMap;

        if (resourceMap && resourceMap?.length > 0) {
            return project?.trackResourceMap?.find(x => x.resourceId === resource.id) !== undefined;
        }
        return false;
    }

    function isTrackIdMapped(trackId: number) {
        return project?.trackResourceMap?.find(x => x.trackId === trackId) !== undefined;
    }

    function getSelectedProjectResources() {
        return project?.resources;
    }

    function getSelectedProjectReleaseInfo() {
        return project?.releaseInfo;
    }

    function isOverrideChecked() {
        if (project) {

            return project.overrideRestrictions?.find(x => x === "*") !== undefined;
        }
        return false;
    }

    function getUnmappedResources(): Resource[] {
        const selectedProjectResources = getSelectedProjectResources();
        const x = selectedProjectResources!.filter(x => !isResourceMapped(x)).values();
        return Array.from(x);
    }

    function getMappedResources(passedProject?: Project): Resource[] {
        const targetProject = passedProject ?? project;
        const resources = targetProject?.resources ?? [];

        const x = resources!.filter(x => isResourceMapped(x)).values();
        return Array.from(x);
    }

    // Get mapped resource if track asset mode, otherwise all resources
    function getEffectiveResources(passedProject?: Project): Resource[] {
        const targetProject = passedProject ?? project;
        const resources = targetProject?.resources?.filter(r => !r.deleted);

        if (isAudioMode(resources)) {
            return getMappedResources(project);
        }
        return resources || [];
    }

    function getMappedResource(track: ITrack): Resource | undefined {
        const mapping = project?.trackResourceMap?.find(x => x.trackId === track.number);
        return project?.resources?.filter(x => !x.deleted).find(x => x.id === mapping?.resourceId);
    }

    const getFilesAddedEvent = (resources: Resource[]): FilesAddedEvent => {
        const newStateChangeEvent: FilesAddedEvent = {
            resourcesAdded: 0,
            resourcesReplaced: 0,
            resourcesRemoved: 0,
            tooManyResources: 0,
        };
        const tempResources = [...project?.resources ?? []];

        for (const resource of resources) {
            if (tempResources.find(x => x.id === resource.id)) {
                newStateChangeEvent.resourcesReplaced++;
                tempResources.push(resource);
            } else {
                if (tempResources.length < trackMapConfig.maxResourcesInList) {
                    tempResources.push(resource);
                    newStateChangeEvent.resourcesAdded++;
                } else {
                    newStateChangeEvent.tooManyResources++;
                }
            }
        }
        return newStateChangeEvent;
    };

    // Called from aspera when all resources for a project are uploaded
    async function startIngest(updatedProject: Project, source: string, selectedCprsShipments?: string[]): Promise<Project> {
        // This flag isn't saved in the DB.  Disable is in the cache so that it
        // can't be retriggered on a refresh
        updatedProject.startIngestion = false;
        queryClient.setQueryData(["project", updatedProject.projectId!], updatedProject);

        const validateResponse = await ingest(updatedProject, source, selectedCprsShipments);

        updatedProject.ingestHasStarted = true;

        if (validateResponse) {
            updatedProject = await setJobId(updatedProject.projectId!, validateResponse, true);
        }
        return updatedProject;
    }
    return {
        duplicateProject,

        // crud
        deleteProject,

        // productInfo
        setProjectIdentifier,
        setProjectNotes,
        getSelectedProjectReleaseInfo,
        getTracks,
        getTrackGroups,

        // resources
        getSelectedProjectResources,
        deleteResources,
        getUnmappedResources,
        getMappedResources,
        getEffectiveResources,
        getMappedResource,
        getFilesAddedEvent,
        addResources,

        // mapping
        isTrackIdMapped,
        getMappedTrack,
        resetMap,
        mapResourceToTrack,
        mapResourcesToTrack,
        unmapResource,

        revalidateProject,

        setIsIngestRequestedAfterUpload,
        setOverrideRestrictions,
        isOverrideChecked,
        startIngest,
        project,
        batchDelete,

        updateUploadStates,
        recentJobFinished,
    };
};
